import { apiFetch, ensureArcadiaCsrf } from '@/lib/api-fetch';
import type { Profile as AppProfile } from '@/types';

export interface AuthUser {
  id: string;
  walletAddress: string;
  email?: string;
}

export type Profile = AppProfile;

export type AuthSessionPayload = { user: AuthUser; profile: Profile };

const authListeners = new Set<(data: AuthSessionPayload | null) => void>();

function emitAuthState(data: AuthSessionPayload | null): void {
  for (const fn of authListeners) {
    try {
      fn(data);
    } catch (e) {
      console.error('Auth listener error:', e);
    }
  }
}

/** Subscribe to sign-in / sign-out from `signInWithWalletAddress` and `signOut`. */
export function onAuthStateChange(callback: (user: AuthSessionPayload | null) => void): () => void {
  authListeners.add(callback);
  return () => {
    authListeners.delete(callback);
  };
}

function mapProfile(p: Record<string, unknown>): Profile {
  return {
    id: p.id as string,
    walletAddress: (p.walletAddress as string) || (p.wallet_address as string) || '',
    username: (p.username as string) || '',
    displayName: (p.displayName as string) || (p.display_name as string) || '',
    avatarUrl: (p.avatarUrl as string) || (p.avatar_url as string) || undefined,
    bannerUrl: (p.bannerUrl as string) || (p.banner_url as string) || undefined,
    level: Number(p.level ?? 0),
    xp: Number(p.xp ?? 0),
    xpToNext: Number(p.xpToNext ?? p.xp_to_next ?? 0),
    rankTitle: (p.rankTitle as string) || (p.rank_title as string) || '',
    country: (p.country as string) || undefined,
    bio: (p.bio as string) || undefined,
    steamLikeSummary: (p.steamLikeSummary as string) || (p.steam_like_summary as string) || undefined,
    socialLinks: (p.socialLinks as Profile['socialLinks']) || (p.social_links as Profile['socialLinks']) || {},
    isAdmin: (p.isAdmin as boolean) === true || (p.is_admin as boolean) === true,
    arcadiaPointsBalance: Number(p.arcadiaPointsBalance ?? p.arcadia_points_balance ?? 0),
    stats: {
      totalPlaytimeHours: Number((p.stats as Record<string, unknown>)?.totalPlaytimeHours ?? p.total_playtime_hours ?? 0),
      gamesOwned: Number((p.stats as Record<string, unknown>)?.gamesOwned ?? p.games_owned_count ?? 0),
      achievementsUnlocked: Number(
        (p.stats as Record<string, unknown>)?.achievementsUnlocked ?? p.achievements_unlocked_count ?? 0
      ),
      streamsWatched: Number((p.stats as Record<string, unknown>)?.streamsWatched ?? p.streams_watched_count ?? 0),
      streamsHosted: Number((p.stats as Record<string, unknown>)?.streamsHosted ?? p.streams_hosted_count ?? 0),
    },
  };
}

/** Rotates access + CSRF cookies from HttpOnly refresh when the short-lived JWT expired. */
async function tryRefreshSessionCookies(): Promise<boolean> {
  try {
    await ensureArcadiaCsrf();
    const res = await apiFetch('/api/auth/refresh', { method: 'POST' });
    if (!res.ok) return false;
    await ensureArcadiaCsrf();
    return true;
  } catch {
    return false;
  }
}

export async function getCurrentUser(): Promise<AuthSessionPayload | null> {
  let res = await apiFetch('/api/auth/me');
  if (res.status === 401) {
    const refreshed = await tryRefreshSessionCookies();
    if (refreshed) {
      res = await apiFetch('/api/auth/me');
    }
  }
  if (!res.ok) return null;
  const data = (await res.json()) as { user: AuthUser; profile: Record<string, unknown> };
  if (!data.user || !data.profile) return null;
  return { user: data.user, profile: mapProfile(data.profile) };
}

export async function signInWithWalletAddress(walletAddress: string): Promise<AuthSessionPayload> {
  await ensureArcadiaCsrf();
  const response = await apiFetch('/api/auth/wallet/connect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ walletAddress }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || 'Failed to sign in');
  }
  await ensureArcadiaCsrf();
  const data = (await response.json()) as { user: AuthUser; profile: Record<string, unknown> };
  const session: AuthSessionPayload = {
    user: { id: data.user.id, walletAddress: data.user.walletAddress },
    profile: mapProfile(data.profile),
  };
  emitAuthState(session);
  return session;
}

async function handleAuthResponse(response: Response, fallbackMessage: string): Promise<AuthSessionPayload> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || fallbackMessage);
  }
  await ensureArcadiaCsrf();
  const data = (await response.json()) as { user: AuthUser; profile: Record<string, unknown> };
  const session: AuthSessionPayload = {
    user: { id: data.user.id, walletAddress: data.user.walletAddress || '', email: data.user.email },
    profile: mapProfile(data.profile),
  };
  emitAuthState(session);
  return session;
}

export async function signInWithEmail(email: string, password: string): Promise<AuthSessionPayload> {
  await ensureArcadiaCsrf();
  const response = await apiFetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleAuthResponse(response, 'Failed to log in');
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string
): Promise<AuthSessionPayload> {
  await ensureArcadiaCsrf();
  const response = await apiFetch('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, displayName }),
  });
  return handleAuthResponse(response, 'Failed to create account');
}

export async function signInWithGoogleToken(idToken: string): Promise<AuthSessionPayload> {
  await ensureArcadiaCsrf();
  const response = await apiFetch('/api/auth/google', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  return handleAuthResponse(response, 'Failed to sign in with Google');
}

export async function signOut(): Promise<void> {
  await ensureArcadiaCsrf();
  await apiFetch('/auth/sign-out', { method: 'POST' });
  emitAuthState(null);
}
